import {
  addDays,
  addMinutes,
  differenceInCalendarDays,
  differenceInDays,
  format,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay
} from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/native';

import { useFocusEffect } from '@react-navigation/core';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { calculateStudyParticipantDates } from '../../common/calculateStudyParticipantDates';
import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import CalendarDayScroll, {
  CalendarDayScrollEvent
} from '../../components/CalendarDayScroll';
import ListItem from '../../components/list/ListItem';
import ListItemAccessory from '../../components/list/ListItemAccessory';
import ListItemIcon from '../../components/list/ListItemIcon';
import ListItemText from '../../components/list/ListItemText';
import Spacer from '../../components/Spacer';
import { B, H5, P, H3, Caption } from '../../components/Typography';
import Colors from '../../constants/Colors';
import { useUserContext } from '../../context/UserContext';
import { StudyParticipant } from '../../models/StudyParticipant';
import {
  getOccurrenceExpirationDate,
  getStreakFromOccurrences,
  ParticipantEventOccurrence
} from '../study/helpers/ParticipantEventHelpers';
import EncouragementView from '../../components/EncouragementView';
import TodayEmptyStateView from './TodayEmptyStateView';
import { Animated } from 'react-native';
import { useAppContext } from '../../AppContext';
import { useGetStudyIntake } from '../../hooks/study/useGetStudyIntake';
import { getDayNightBackgroundSource } from '../../common/DayNightUtils';
import AppVersionInfo from '../../components/AppVersionInfo';
import { StudyTemplateEventType } from '../../models/ParticipantEvent';
import elementIDs from '../../../elementIDs';
import {
  gActiveStudyParticipant,
  isJoinableStudyParticipant,
  selectPrioritizedStudyParticipant
} from '../../hooks/study/useHasActiveStudy';

const StyledScreenContainerView = styled.View`
  background-color: white;
  flex: 1 1 auto;
`;

const StyledSquareImage = styled.Image`
  height: 35px;
  width: 35px;
`;

const StyledB = styled(B)`
  color: ${Colors.secondaryText};
`;

const StyledDates = styled.View`
  align-items: flex-end;
`;

const StyledStartDate = styled(B)`
  color: ${Colors.secondaryText};
  text-align: right;
`;

const StyledExpiresDate = styled(Caption)`
  color: ${Colors.error};
  text-align: right;
`;

const StyledListItem = styled(ListItem)<{ opacity: number }>`
  min-height: 60px;
  margin-bottom: 12px;
  opacity: ${p => p.opacity};
`;

const StyledEventListView = styled(Animated.ScrollView)`
  width: 100%;
  height: 100%;
  background-color: #fff;
`;

const StyledListContainer = styled.View`
  padding: 32px 12px;
  flex: 1;
`;

const StyledHeading = styled(H5)`
  margin: 12px 0px 16px 0px;
`;

const StyledAnytimeFormsSubheading = styled(Caption)`
  color: ${Colors.secondaryText};
  margin: -8px 0px 16px 0px;
`;

const StyledHeadingView = styled.View`
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const StyledTodayChip = styled.TouchableOpacity`
  padding: 3px 11px;
  border-radius: 32px;
  background-color: #e6e3fe;
`;

const StyledStudyStreakSectionContainer = styled.View`
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const StyledStreakView = styled.View`
  flex-direction: row;
  align-items: center;
  margin: 4px 12px;
`;

const StyledStreakText = styled(B)`
  margin-left: 8px;
`;

const StyledStudySectionNameView = styled.View`
  padding-right: 12px;
`;

const iconPack = [
  { name: 'Sleep', source: require('../../assets/icons/icon-sleep.png') },
  { name: 'Mood', source: require('../../assets/icons/icon-mood.png') },
  { name: 'Pain', source: require('../../assets/icons/icon-pain.png') },
  {
    name: 'Brain / Focus',
    source: require('../../assets/icons/icon-brain-focus.png')
  },
  { name: 'Skin', source: require('../../assets/icons/icon-skin.png') },
  { name: 'Gut', source: require('../../assets/icons/icon-gut.png') },
  {
    name: 'Stress / Anxiety',
    source: require('../../assets/icons/icon-stress-anxiety.png')
  },
  { name: 'Focus', source: require('../../assets/icons/icon-focus.png') },
  { name: 'Itch', source: require('../../assets/icons/icon-itch.png') },
  { name: 'Practice', source: require('../../assets/icons/icon-practice.png') },
  { name: 'Product', source: require('../../assets/icons/icon-product.png') },
  { name: 'Photo', source: require('../../assets/icons/icon-photo.png') },
  { name: 'Survey', source: require('../../assets/icons/icon-survey.png') },
  {
    name: 'Brain Game',
    source: require('../../assets/icons/icon-brain-game.png')
  }
];

interface StudyParticipantToDoState {
  studyParticipant: StudyParticipant;
  startDate?: Date;
  endDate?: Date;
  studyLength: number;
  isCompletedStudy: boolean;
}

const ToDoScreen: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const modalNavigation = useNavigation<StackNavigationProp<any>>();
  const { loadingStudyIntake, loadingStudyIntakeError, getStudyIntake } =
    useGetStudyIntake();
  // get study participants on load
  const {
    user,
    loadingUser,
    studyParticipants,
    loadingStudyParticipants,
    pastOccurrences,
    completedOccurrences,
    expiredOccurrences,
    futureOccurrences,
    allOccurrences,
    persistentOccurrences,
    updateUserSession
  } = useUserContext();

  const { setHeaderConfig, scrollY } = useAppContext();

  useFocusEffect(
    useCallback(() => {
      if (user?.id) updateUserSession();
    }, [user?.id, updateUserSession])
  );
  const [mounted, setMounted] = useState(false);
  const [state, setState] = useState<StudyParticipantToDoState>();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const selectStudyParticipant = useCallback(
    (
      studyParticipant: StudyParticipant,
      calculatedDates?: ReturnType<typeof calculateStudyParticipantDates>
    ) => {
      // console.log('studyParticipant', studyParticipant);
      const { study } = studyParticipant;
      if (!calculatedDates) {
        calculatedDates = calculateStudyParticipantDates(
          study,
          studyParticipant
        );
      }
      const {
        startDate,
        endDate,
        studyLength,
        projectedStudyLength,
        isCompletedStudy
      } = calculatedDates;
      let currentDay = startDate ? differenceInDays(new Date(), startDate) : 0;
      if (currentDay < 0) currentDay = studyLength;
      setHeaderConfig({
        title: study?.name,
        backgroundSource: getDayNightBackgroundSource(), // Note: Roy would like to just use day/night background on Today tab
        caption: startDate
          ? `Day ${currentDay + 1} of ${projectedStudyLength ?? studyLength}`
          : 'Not Started'
      });
      setState({
        studyParticipant,
        startDate,
        endDate,
        studyLength: projectedStudyLength ?? studyLength,
        isCompletedStudy
      });
      // selected date should be within range of the study start and end dates
      setSelectedDate(date => {
        if (startDate && isBefore(date, startDate)) {
          return startDate;
        } else if (endDate && isAfter(date, endDate)) {
          return endDate;
        }
        return date;
      });
    },
    [setHeaderConfig]
  );
  // Get first non-completed study participant record on initial load
  useEffect(() => {
    setMounted(true);
    // get tab navigation to get current tab
    const state = navigation.getParent()?.getState();
    const focusedRoute = state?.routes[state.index].name || '';
    if (focusedRoute !== 'ToDoNav') return;
    setHeaderConfig(undefined);
    if (studyParticipants) {
      const selected = selectPrioritizedStudyParticipant(studyParticipants);
      if (selected) {
        selectStudyParticipant(
          selected,
          calculateStudyParticipantDates(selected.study, selected)
        );
      } else {
        setState(undefined);
      }
    }
  }, [navigation, setHeaderConfig, selectStudyParticipant, studyParticipants]);

  // get all selected days occurences for display
  const {
    persistentEvents,
    todayActiveOccurrences,
    todayExpiringSoonOccurrences,
    todayCompletedOccurrences,
    todayExpiredOccurrences,
    todayFutureOccurrences,
    calendarDays,
    streak,
    lastStudyTemplateSection
  } = useMemo(() => {
    const activeStudyParticipant = gActiveStudyParticipant(
      studyParticipants || [],
      allOccurrences
    );
    const calendarDays: CalendarDayScrollEvent[] = [];
    const persistentEvents = persistentOccurrences.filter(o => {
      const formTemplateId = o.participantEvent.formTemplateId;
      const qualificationFormTemplateId =
        o.studyParticipant?.study?.qualificationFormTemplateId;
      const isQualificationForm =
        formTemplateId === qualificationFormTemplateId;
      return (
        activeStudyParticipant?.study &&
        activeStudyParticipant.study.id === o.studyParticipant?.study?.id &&
        o.participantEvent.allowMultipleEntries &&
        (isSameDay(selectedDate, o.intendedStartDate) ||
          isAfter(selectedDate, o.intendedStartDate)) &&
        !isQualificationForm
      );
    });
    const todayActiveOccurrences = pastOccurrences.filter(o => {
      if (o.studyParticipant?.id === state?.studyParticipant.id) {
        calendarDays.push({ date: o.intendedStartDate, completed: false });
        return isSameDay(o.intendedStartDate, selectedDate);
      }
    });
    const todayExpiringSoonOccurrences = pastOccurrences.filter(o => {
      if (o.studyParticipant?.id === state?.studyParticipant.id) {
        calendarDays.push({ date: o.intendedStartDate, completed: false });
        return (
          !isSameDay(o.intendedStartDate, selectedDate) &&
          isBefore(o.intendedStartDate, selectedDate)
        );
      }
    });
    const todayCompletedOccurrences = completedOccurrences.filter(o => {
      if (
        o.participantEvent.allowMultipleEntries &&
        o.participantEventLog?.completedDate
      ) {
        const event = o.participantEvent;
        if (
          event.participantEventLogs.some(
            log =>
              log.completedDate && isSameDay(log.completedDate, selectedDate)
          )
        ) {
          calendarDays.push({
            date: o.participantEventLog.completedDate,
            completed: true
          });
          return true;
        }
      } else if (o.studyParticipant?.id === state?.studyParticipant.id) {
        calendarDays.push({ date: o.intendedStartDate, completed: true });
        return isSameDay(o.intendedStartDate, selectedDate);
      }
    });
    const todayExpiredOccurrences = expiredOccurrences.filter(o => {
      if (o.participantEvent.allowMultipleEntries) return false;
      if (o.studyParticipant?.id === state?.studyParticipant.id) {
        calendarDays.push({ date: o.intendedStartDate, completed: false });
        // filter for expired events for the selected date
        return isSameDay(selectedDate, o.intendedStartDate);
      }
    });
    const todayFutureOccurrences = futureOccurrences.filter(
      o =>
        o.studyParticipant?.id === state?.studyParticipant.id &&
        isSameDay(o.intendedStartDate, selectedDate)
    );
    const streak = state?.studyParticipant?.id
      ? getStreakFromOccurrences(
          state.studyParticipant.id,
          completedOccurrences,
          expiredOccurrences,
          pastOccurrences
        )
      : 0;
    // get the most recent study template event section name if exists
    const startDate = state?.startDate;
    const appliedStudyTemplates =
      state?.studyParticipant.participantAppliedStudyTemplates?.sort(
        (a, b) => a.startDate.getTime() - b.startDate.getTime()
      );

    const lastStudyTemplateSection = startDate
      ? appliedStudyTemplates?.reduce((result, appliedStudyTemplate) => {
          const sectionName =
            appliedStudyTemplate.studyTemplate.studyTemplateEvents
              ?.filter(e => e.type === StudyTemplateEventType.Section)
              .sort((a, b) => b.position - a.position) // sort descending
              .find(e => {
                const sectionStartDate = addDays(
                  startOfDay(appliedStudyTemplate.startDate),
                  e.daysFromStart
                );
                const sectionEndDate = addMinutes(
                  startOfDay(sectionStartDate),
                  e.duration
                );
                return (
                  isAfter(new Date(), sectionStartDate) &&
                  isBefore(new Date(), sectionEndDate)
                );
              })?.name;
          if (!sectionName) return result;
          return sectionName;
        }, '')
      : '';
    return {
      persistentEvents,
      todayActiveOccurrences,
      todayExpiringSoonOccurrences,
      todayCompletedOccurrences,
      todayExpiredOccurrences,
      todayFutureOccurrences,
      calendarDays,
      streak,
      lastStudyTemplateSection
    };
  }, [
    persistentOccurrences,
    completedOccurrences,
    expiredOccurrences,
    futureOccurrences,
    pastOccurrences,
    selectedDate,
    state?.startDate,
    state?.studyParticipant
  ]);
  const todayCompletedCount = todayCompletedOccurrences.length;
  const todayTotalCount =
    todayCompletedCount +
    todayActiveOccurrences.length +
    todayExpiringSoonOccurrences.length +
    todayExpiredOccurrences.length;
  const isPendingStudy = Boolean(
    state?.studyParticipant?.study?.qualificationFormTemplateId &&
    !state?.studyParticipant?.study?.isPublic &&
    !state?.studyParticipant?.inviteSent
  );

  const isStudyJoinable = isJoinableStudyParticipant(state?.studyParticipant);

  const { calendarStartDate, calendarLength } = useMemo(() => {
    let calendarStartDate = state?.startDate;
    let calendarLength = state?.studyLength;
    if (!calendarStartDate || !calendarLength) return {};
    let dayCount = differenceInDays(new Date(), calendarStartDate);
    // pad at least 7 days in calendar day scroll view
    dayCount = Math.max(7, dayCount);
    calendarStartDate = addDays(new Date(), -dayCount);

    // calculate studyLengthOffset in order to display correct end date
    const startDate = state?.startDate || new Date();
    const studyLengthOffset = differenceInCalendarDays(
      startDate,
      calendarStartDate
    );
    calendarLength = calendarLength + studyLengthOffset;
    if (calendarLength > 1000) calendarLength = 1000;
    return { calendarStartDate, calendarLength };
  }, [state?.startDate, state?.studyLength]) as {
    calendarStartDate?: Date;
    calendarLength?: number;
  };
  return (
    <StyledScreenContainerView>
      {mounted &&
      !loadingUser &&
      state?.studyParticipant &&
      !state?.studyParticipant.disqualified &&
      !state.studyParticipant.startDate ? (
        <TodayEmptyStateView
          title={`Welcome ${user?.firstName}!`}
          description={
            isPendingStudy
              ? "Your study is pending. We'll notify you once it's ready to begin."
              : !isStudyJoinable
                ? "This study isn't currently accepting new participants."
                : 'You are almost ready to start the study. We just need to collect some additional information.'
          }
          buttonLabel='Get Started'
          disabled={loadingStudyIntake}
          loading={loadingStudyIntake}
          error={loadingStudyIntakeError}
          isPendingStudy={isPendingStudy}
          hideButton={!isStudyJoinable}
          onButtonPressed={async () => {
            if (!state?.studyParticipant.id) return;
            const studyIntake = await getStudyIntake(state.studyParticipant.id);
            if (studyIntake) {
              modalNavigation.navigate('ModalNavigator', {
                screen: 'FormTemplate',
                params: {
                  formTemplateId: studyIntake?.formTemplateId,
                  entityDataId: studyIntake?.id,
                  studyParticipantId: state.studyParticipant.id
                }
              });
            }
          }}
        />
      ) : (mounted &&
          !loadingUser &&
          !loadingStudyParticipants &&
          !state?.studyParticipant) ||
        (studyParticipants && allOccurrences.length === 0) ? (
        <TodayEmptyStateView
          title={`Welcome ${user?.firstName}!`}
          description='Sign up for a Chloe clinical or self study and start learning about
          your health!'
          buttonLabel='Discover Studies'
          onButtonPressed={() => {
            navigation.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: { screen: 'DiscoverNav' }
            });
          }}
        />
      ) : mounted && !loadingUser && !loadingStudyParticipants ? (
        <StyledEventListView
          scrollIndicatorInsets={{ right: 1 }}
          contentContainerStyle={{
            minHeight: 768,
            flexGrow: 1 // Fill the dialog
          }}
          // get scroll offset for app header animation
          onScroll={Animated.event(
            [{ nativeEvent: { contentOffset: { y: scrollY } } }],
            { useNativeDriver: false }
          )}
          scrollEventThrottle={18}
        >
          <StyledStudyStreakSectionContainer>
            <StyledStreakView>
              <H3>{streak === 0 ? '🌧' : '🔥'}</H3>
              <StyledStreakText testID={elementIDs.dayStreakID} color='dark'>
                {streak} day streak
              </StyledStreakText>
            </StyledStreakView>
            {!!lastStudyTemplateSection && (
              <StyledStudySectionNameView>
                <B color='dark'>{lastStudyTemplateSection}</B>
              </StyledStudySectionNameView>
            )}
          </StyledStudyStreakSectionContainer>
          <EncouragementView />
          <Spacer size={2} />
          {calendarStartDate && calendarLength && (
            <CalendarDayScroll
              startDate={calendarStartDate}
              length={calendarLength}
              events={calendarDays}
              selectedDay={selectedDate}
              onSelect={date => setSelectedDate(date)}
            />
          )}
          <StyledListContainer>
            <StyledHeadingView>
              <H5 color='dark'>Today's Activities</H5>
              {isSameDay(selectedDate, new Date()) ? (
                <P color='dark'>
                  {`${todayCompletedCount}/${todayTotalCount} Complete`}
                </P>
              ) : (
                <StyledTodayChip
                  onPress={() => {
                    setSelectedDate(new Date());
                  }}
                >
                  <B color='dark'>Today</B>
                </StyledTodayChip>
              )}
            </StyledHeadingView>
            <Spacer size={2} />
            {todayActiveOccurrences.map((occurrence, index) => (
              <ToDoScreenListItem
                key={index}
                status='past'
                occurrence={occurrence}
                selectedDate={selectedDate}
              />
            ))}
            {todayExpiringSoonOccurrences.length > 0 && (
              <>
                <StyledHeading color='dark'>Expiring Soon</StyledHeading>
                {todayExpiringSoonOccurrences.map((occurrence, index) => (
                  <ToDoScreenListItem
                    key={index}
                    status='expiringSoon'
                    occurrence={occurrence}
                    selectedDate={selectedDate}
                  />
                ))}
              </>
            )}
            {persistentEvents.length > 0 && (
              <>
                <StyledHeading color='dark'>Anytime Forms</StyledHeading>
                <StyledAnytimeFormsSubheading>
                  These forms will remain available for the whole study.
                </StyledAnytimeFormsSubheading>
                {persistentEvents.map((occurrence, index) => (
                  <ToDoScreenListItem
                    key={index}
                    status='persistent'
                    occurrence={occurrence}
                    selectedDate={selectedDate}
                  />
                ))}
              </>
            )}
            {todayCompletedOccurrences.length > 0 && (
              <>
                <StyledHeading color='dark'>Completed</StyledHeading>
                {todayCompletedOccurrences.map((occurrence, index) => (
                  <ToDoScreenListItem
                    key={index}
                    status='completed'
                    occurrence={occurrence}
                    selectedDate={selectedDate}
                  />
                ))}
              </>
            )}
            {todayExpiredOccurrences.length > 0 && (
              <>
                <StyledHeading color='dark'>Expired</StyledHeading>
                {todayExpiredOccurrences.map((occurrence, index) => (
                  <ToDoScreenListItem
                    key={index}
                    status='expired'
                    occurrence={occurrence}
                    selectedDate={selectedDate}
                  />
                ))}
              </>
            )}
            {todayFutureOccurrences.length > 0 && (
              <>
                <StyledHeading color='dark'>Future</StyledHeading>
                {todayFutureOccurrences.map((occurrence, index) => (
                  <ToDoScreenListItem
                    key={index}
                    status='future'
                    occurrence={occurrence}
                    selectedDate={selectedDate}
                  />
                ))}
              </>
            )}
          </StyledListContainer>
          <AppVersionInfo />
        </StyledEventListView>
      ) : (
        <></>
      )}
      {(!studyParticipants || loadingStudyParticipants || loadingUser) && (
        <ActivityIndicatorScreen modal />
      )}
    </StyledScreenContainerView>
  );
};

const ToDoScreenListItem: React.FC<{
  status:
    | 'expiringSoon'
    | 'past'
    | 'completed'
    | 'expired'
    | 'future'
    | 'persistent';
  occurrence: ParticipantEventOccurrence;
  selectedDate: Date;
}> = ({ status, occurrence, selectedDate }) => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const studyParticipant = occurrence?.studyParticipant;
  const formTemplate = occurrence.participantEvent.formTemplate;
  const appIconName = formTemplate?.metadata?.appIconName;
  const study = studyParticipant?.study;
  return (
    <StyledListItem
      framed
      filled
      opacity={
        status === 'persistent' ||
        ((status === 'past' || status === 'expiringSoon') &&
          study?.status !== 'Locked' &&
          study?.status !== 'Completed')
          ? 1
          : 0.7
      }
      disabled={
        (status === 'persistent' && isAfter(selectedDate, new Date())) ||
        status === 'completed' ||
        status === 'expired' ||
        isAfter(selectedDate, new Date()) ||
        study?.status === 'Locked' ||
        study?.status === 'Completed'
      }
      onPress={() => {
        if (occurrence.studyParticipant) {
          const params = {
            studyParticipantId: studyParticipant?.id,
            participantEventId: occurrence.participantEvent.id,
            participantEventLogId: occurrence.participantEventLog?.id,
            occurrenceCount: occurrence.occurrenceCount
          };
          if (occurrence.participantEvent.allowMultipleEntries) {
            const eventLogs =
              occurrence.participantEvent.participantEventLogs.sort(
                (a, b) => b.id - a.id
              );
            const lastLog =
              eventLogs.length > 0
                ? eventLogs[0]
                : occurrence.participantEventLog;
            params.participantEventLogId = lastLog?.completedDate
              ? undefined
              : lastLog?.id;
          }
          navigation.push('StudyEventForm', params);
        }
      }}
    >
      {appIconName && (
        <ListItemIcon>
          <StyledSquareImage
            resizeMode='contain'
            source={
              iconPack.find(icon => {
                {
                  return icon.name === appIconName;
                }
              })?.source || ''
            }
          />
        </ListItemIcon>
      )}
      <ListItemText
        accessoryPadding={status === 'expiringSoon' ? 140 : 64}
        primary={occurrence.participantEvent.name}
        primaryTextColor={Colors.secondaryText}
        primaryTextNumberOfLines={2}
      />
      <ListItemAccessory>
        {status === 'expiringSoon' ? (
          <StyledDates>
            <StyledStartDate>
              {format(occurrence.intendedStartDate, 'MMM dd')}
            </StyledStartDate>
            <StyledExpiresDate>
              {`Expires ${format(
                getOccurrenceExpirationDate(
                  occurrence.intendedStartDate,
                  occurrence.participantEvent.expireDayCount
                ),
                'MMM dd'
              )}`}
            </StyledExpiresDate>
          </StyledDates>
        ) : status === 'past' ? (
          <StyledB color='dark'>
            {occurrence.participantEvent.studyTemplateEvent?.duration
              ? `${occurrence.participantEvent.studyTemplateEvent.duration} min`
              : ''}
          </StyledB>
        ) : (
          <></>
        )}
      </ListItemAccessory>
    </StyledListItem>
  );
};

export default ToDoScreen;
